import React, { useState, useEffect,useRef } from 'react';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import ProfileCustomClearIcon from '../../../HelperIcons/ProfileCustomClearIcon';
import ProfileCustomDeleteIcon from '../../../HelperIcons/ProfileCustomDeleteIcon';
import Popper from "@mui/material/Popper";
import Stack from '@mui/material/Stack';
import { styled } from '@mui/system';
import { ImagePath } from '../../../../../../assets/ImagePath';

const PopperContainer = styled(Popper)(() => ({
	"& .MuiAutocomplete-paper": {
		marginTop: "4px",
		boxShadow: "1px 2px 10px rgba(0, 0, 0, 0.35)",
    width: '300px',
    height: '200px',
    overflowY: 'auto',
	},
  "& .MuiAutocomplete-listbox": {
    height: 'inherit',
    overflowY: 'auto',
  },
  "& .MuiAutocomplete-option.Mui-focused": {
    backgroundColor: "#F1F1F1 !important",
  },
  "& .MuiAutocomplete-option[aria-selected='true']": {
    backgroundColor: "#F1F1F1 !important",
  },
	"& .MuiAutocomplete-option": {
		paddingRight: "7px !important",
	},
}));

const PopperMy = function (props) {
	return <PopperContainer {...props} placement="bottom-start" />;
};

const satellitePhoneNumberPatterns = [
  { provider: "Inmarsat", providerCode: "+870", regex: /^\+870\d{9}$/ },
  { provider: "Thuraya", providerCode: "+88216", regex: /^\+88216\d{7}$/ },
  { provider: "Others", providerCode: "+882", regex: /^\+882\d{7,10}$/ },
  { provider: "Others", providerCode: "+883", regex: /^\+883\d{7,10}$/ },
  { provider: "ICO Global", providerCode: "+8810", regex: /^\+8810\d{7}$|^\+8811\d{7}$/ },
  { provider: "Ellipso", providerCode: "+8812", regex: /^\+8812\d{7}$|^\+8813\d{7}$/ },
  { provider: "Teledesic", providerCode: "+8814", regex: /^\+8814\d{7}$|^\+8815\d{7}$/ },
  { provider: "Iridium", providerCode: "+8816", regex: /^\+8816\d{7}$|^\+8817\d{7}$/ },
  { provider: "Globalstar", providerCode: "+8818", regex: /^\+8818\d{7}$|^\+8819\d{7}$/ },
  { provider: "Custom", providerCode: "+", regex: /^\+?\d{7,15}$/ },
];

const CustomSatelliteNumberField = ({ field, index, handleFieldChange, handleClearField, handleDeleteField, shudDeleteBtnRender, shudClearBtnRender }) => {
  const [error, setError] = useState(false);
  const [helperText, setHelperText] = useState('');
  const [inputValue, setInputValue] = useState(field.value);
  const [selectedProvider, setSelectedProvider] = useState(() => {
    const matchedProvider = satellitePhoneNumberPatterns.find(pattern => inputValue.startsWith(pattern.providerCode) || pattern.providerCode.startsWith(inputValue));
    if (matchedProvider) {
      return matchedProvider;
    } else {
      return satellitePhoneNumberPatterns[0];
    }
  });
  const isFirstRender = useRef(true);

  useEffect(() => {
    setHelperText(error ? 'Phone number is invalid.': '');
  },[error]);

  useEffect(() => {
    const isValid = satellitePhoneNumberPatterns.some(pattern => pattern.regex.test(inputValue));
    field.isInvalid = !isValid;
    if (!field.isPristine) setError(!isValid);
    let matchedProvider;
    if (inputValue === '+') {
      matchedProvider = satellitePhoneNumberPatterns[satellitePhoneNumberPatterns.length - 1];
    } else {
      matchedProvider = satellitePhoneNumberPatterns.find(pattern => inputValue.startsWith(pattern.providerCode) || pattern.providerCode.startsWith(inputValue));
    }
    if (matchedProvider) {
      setSelectedProvider(matchedProvider);
    } else {
      setSelectedProvider(satellitePhoneNumberPatterns[0]);
    }
    if (!inputValue) {
      setInputValue(matchedProvider.providerCode);
    }
    handleFieldChange(index, { target: { value: inputValue }}, isFirstRender.current);
    if (isFirstRender.current) isFirstRender.current = false;
  }, [inputValue]);

  const handleInputChange = (event) => {
    let { value } = event.target;
    value = '+' + value.replace(/\+/g, ''); // Ensure only one plus sign at the start
    const regex = /^[+][0-9]*$/; // Allow only digits and an optional plus sign at the start
    if (regex.test(value)) {
      setInputValue(value);
    }
  };

  const handleProviderChange = (event, newValue) => {
    if (newValue) {
      setSelectedProvider(newValue);
      setInputValue(newValue.providerCode);
    }
  };

  return (
    <Stack direction="row" alignItems="center" spacing={2} width="100%" sx={{ borderBottom: '1px solid #E7E7FF' }}>
      <Autocomplete
        options={satellitePhoneNumberPatterns}
        sx={{
          paddingRight: '10px',
          borderRight: '1px solid #E7E7FF',
          }}
        PopperComponent={PopperMy}
        disableClearable
        getOptionLabel={(option) => `${option.provider} ${option.providerCode}`}
        popupIcon={<img src={ImagePath.downArrow} alt="down arrow" style={{ width: '9px', height: '5px' }} />}
        renderOption={(props, option) => (
          <li {...props}>
            {option.provider} <span style={{ color: '#6b6b6b', marginLeft: '5px' }}>{option.providerCode}</span>
          </li>
        )}
        value={selectedProvider}
        disabled={field.disabled}
        onChange={handleProviderChange}
        renderInput={(params) => (
          <TextField
            {...params}
            variant="standard"
            sx={{
              width: '60px',
              border: 'none',
              '& .MuiInputBase-root': {
                border: 'none',
                paddingRight: '5px !important',
              },
              '& .MuiInputBase-root:before': {
                border: 'none !important',
              },
              '& .MuiInputBase-root:after': {
                border: 'none !important',
              },
              '& .MuiInputBase-input': {
                border: 'none',
              },
            }}
          />)}
      />
      <TextField
        key={field.id}
        value={inputValue}
        disabled={field.disabled}
        onChange={handleInputChange}
        sx={{
           marginLeft: '0 !important',
          '& .MuiInput-underline:before': {
            borderBottom: 'none',
          },
          '& .MuiInput-underline:after': {
            borderBottom: 'none',
          },
          '& .MuiInputBase-root.MuiInputBase-root.MuiInputBase-root:before': {
            borderBottom: 'none',
          },
          '& .MuiInputBase-input': {
            backgroundColor: 'transparent',
            borderRadius: '5px',
            padding: '8px 0 8px',
          },
          '& .MuiInputBase-root': {
            fontWeight: '400',
            fontSize: '14px',
            fontFamily: 'Figtree-Regular',
            borderColor: error ? 'red' : 'inherit',
          },
          '& .MuiFormHelperText-root': {
            position: 'absolute',
            bottom: 0,
            color: '#de0000 !important',
            backgroundColor: 'white',
            fontFamily: 'Figtree-Regular',
          },
          padding: '10px',
          outline: 0,
        }}
        variant="standard"
        fullWidth
        helperText={helperText}
        error={error}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end" sx={{ marginRight: '10px' }}>
              {shudClearBtnRender && (
                <IconButton
                  aria-label="clear"
                  onClick={(e) => {setInputValue(''); handleClearField(index, e);}}
                  disabled={field.disabled}
                  edge="end"
                  sx={{ marginRight: '8px' }}
                >
                  <ProfileCustomClearIcon
                    sx={{
                      backgroundColor: field.disabled ? 'transparent' : 'lightgrey',
                      color: 'white',
                      borderRadius: '50%',
                      width: '18px',
                      height: '18px',
                    }}
                  />
                </IconButton>
              )}
              {shudDeleteBtnRender && (
                <IconButton
                  aria-label="delete"
                  onClick={() => handleDeleteField(index)}
                  disabled={field.disabled}
                  edge="end"
                  sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                >
                  <ProfileCustomDeleteIcon
                    sx={{ width: '20px', height: '20px' }}
                    color={field.disabled ? 'transparent' : '#F74646'}
                  />
                </IconButton>
              )}
            </InputAdornment>
          ),
        }}
      />
    </Stack>
  );
};

export default CustomSatelliteNumberField;
